import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Mail, Phone, UserCheck } from 'lucide-react';
import { apiGet, apiPost } from '../../lib/api.js';
import { useAuth } from '../../store/auth.js';
import { initials } from '../../lib/format.js';
import { disconnectPrompt } from '../../lib/teamHierarchy.js';
import ConfirmDialog from '../../components/ui/ConfirmDialog.jsx';

/**
 * "My Supervisor": the agent above you, with a Disconnect action. Renders
 * nothing for agents who have no supervisor.
 * @returns {JSX.Element|null}
 */
export default function SupervisorSection() {
  const qc = useQueryClient();
  const { agent, setSession, token } = useAuth();
  const [asking, setAsking] = useState(false);

  const { data } = useQuery({ queryKey: ['team-supervisor'], queryFn: () => apiGet('/team/supervisor'), staleTime: 30_000 });
  const sup = data?.supervisor;

  const disconnect = useMutation({
    mutationFn: () => apiPost('/team/supervisor/disconnect'),
    onSuccess: () => {
      setAsking(false);
      // Reflect the cut locally so Team Admin / invite gating updates without a reload.
      if (agent) setSession(token, { ...agent, uplineId: null, parentUserId: null, isTeamAdmin: false });
      qc.invalidateQueries({ queryKey: ['team-supervisor'] });
      qc.invalidateQueries({ queryKey: ['team-downline'] });
      toast.success('You are no longer connected to your supervisor');
    },
    onError: (e) => toast.error(e?.response?.data?.error || 'Could not disconnect'),
  });

  if (!sup) return null;
  return (
    <section className="panel p-6 space-y-4" data-testid="my-supervisor">
      <header className="flex items-center gap-3">
        <span className="w-11 h-11 rounded-xl bg-civix-purple/20 border border-civix-purple/40 flex items-center justify-center">
          <UserCheck className="w-6 h-6 text-civix-purpleSoft" />
        </span>
        <h2 className="font-display text-xl font-semibold">My Supervisor</h2>
      </header>
      <p className="text-sm text-white/65">The person below has full access to your account.</p>

      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-4 min-w-0">
          <span className="w-14 h-14 rounded-full overflow-hidden bg-civix-gradient text-white text-base font-bold flex items-center justify-center shrink-0">
            {sup.avatar ? <img src={sup.avatar} alt="" className="w-full h-full object-cover" /> : initials(sup.name)}
          </span>
          <div className="min-w-0 space-y-0.5">
            <div className="font-semibold truncate">{sup.name}</div>
            <div className="text-xs text-white/55">{sup.teamName}</div>
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-white/70 pt-1">
              {sup.email && <span className="inline-flex items-center gap-1"><Mail className="w-3.5 h-3.5" />{sup.email}</span>}
              {sup.phone && <span className="inline-flex items-center gap-1"><Phone className="w-3.5 h-3.5" />{sup.phone}</span>}
            </div>
          </div>
        </div>
        <button type="button" onClick={() => setAsking(true)} className="px-4 py-2.5 rounded-md border border-red-400/60 text-red-300 text-sm font-bold hover:bg-red-500/10">
          Disconnect from Supervisor
        </button>
      </div>

      <ConfirmDialog
        open={asking}
        danger
        message={disconnectPrompt(sup.name)}
        confirmLabel="Disconnect"
        busy={disconnect.isPending}
        onCancel={() => setAsking(false)}
        onConfirm={() => disconnect.mutate()}
      />
    </section>
  );
}
